<template>
  <section style="padding: 50px 0">
    <div class="container">
      <div class="row">
        <div class="col-lg-8 mx-auto mb-4 text-center" v-if="loading === true">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        </div>
        <div class="col-lg-8 mx-auto mb-4 text-center" v-if="error !== null">
          <div class="alert alert-danger">{{ error }}</div>
        </div>
        <div class="col-lg-8 mx-auto mb-4" v-if="repoInfo !== null">
          <h2 class="mb-4">
            <a :href="'https://github.com/' + $route.params.owner + '/' + $route.params.repo">
              <i class="fab fa-github" />
            </a>
            {{ $route.params.owner }}/{{ $route.params.repo }}
          </h2>
          <p>This software is dual-licensed. You may choose how to use it.</p>
          <div class="card-deck mb-4">
            <div class="card" :class="{'bg-primary text-white': license === 'free'}">
              <div class="card-body">
                <h5 class="card-title d-flex justify-content-between">
                  {{ humanize(repoInfo.freeLicense) }}
                  <a :href="url(repoInfo.freeLicense)" :class="{'text-white': license === 'free'}">
                    <i class="fas fa-file-contract" />
                  </a>
                </h5>
                <p v-show="repoInfo.freeLicense.substr(0, 3) === 'gpl'">
                  Free for works also licensed under {{ humanize(repoInfo.freeLicense) }}
                </p>
                <p v-show="repoInfo.freeLicense.substr(0, 8) === 'cc-by-nc'">
                  Free for non-commercial use
                </p>
                <div class="btn-group-toggle">
                  <label class="btn btn-outline-primary" v-bind:class="{'disabled': license === 'free' || paymentStatus === 'complete'}">
                    <input type="radio" v-model="license" value="free" />
                    License under {{ humanize(repoInfo.freeLicense) }}
                  </label>
                </div>
              </div>
            </div>
            <div class="card" :class="{'bg-primary text-white': license === 'paid'}">
              <div class="card-body">
                <h5 class="card-title d-flex justify-content-between">
                  Commercial
                  <i class="fas fa-file-contract" />
                </h5>
                <p>${{ repoInfo.priceDollars }}, use it how you like</p>
                <div class="btn-group-toggle">
                  <label class="btn btn-outline-primary" v-bind:class="{'disabled': license === 'paid'}">
                    <input type="radio" v-model="license" value="paid" />
                    License commercially
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="card mb-4" v-show="license === 'free'">
            <div class="card-body">
              <h5 class="card-title">License under {{ humanize(repoInfo.freeLicense) }}</h5>
              <p>You're done!</p>
              <p>
                As long as your software complies with the license terms, you are free to use this software inside it.
                Be sure to look at the full license for full terms -- this page is a summary.
              </p>
            </div>
          </div>
          <div class="card mb-4" v-show="license === 'paid'">
            <div class="card-body">
              <h5 class="card-title">License commercially</h5>
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label for="name">Name</label>
                  <input type="text" class="form-control" id="name" placeholder="Person or company" v-model="name" :disabled="paymentStatus === 'complete'" @keyup.enter="payWithStripe()" />
                </div>
                <div class="form-group col-md-6">
                  <label for="email">Email</label>
                  <input type="email" class="form-control" id="email" placeholder="Email" v-model="email" :disabled="paymentStatus === 'complete'" @keyup.enter="payWithStripe()" />
                </div>
              </div>
              <button class="btn btn-outline-primary" @click="payWithStripe()" :disabled="paymentStatus === 'loading' || paymentStatus === 'complete'" :class="{'disabled': paymentStatus === 'loading' || paymentStatus === 'complete'}">
                <i class="fab fa-stripe-s" v-show="paymentStatus === 'notStarted'" />
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-show="paymentStatus === 'loading'" />
                <i class="fas fa-check" v-show="paymentStatus === 'complete'" />
                Pay with Stripe
              </button>
            </div>
          </div>
          <div class="card mb-4" v-show="paymentStatus === 'complete'">
            <div class="card-body">
              <h5 class="card-title">Complete</h5>
              <p class="lead">You're all set.</p>
              <p>Your license has been emailed to you, and you can start using this software right away.</p>
            </div>
          </div>
          <div class="card mb-4" v-if="currentUser && repoInfo && currentUser.uid == repoInfo.licenser.userId">
            <div class="card-body">
              <h5 class="card-title">This is your page</h5>
              <p class="lead">
                Link people here from your README to purchase a commercial license.
              </p>
              <p>
                <b class="mr-1">Need to edit?</b>
                <a href="/">Start again</a> with no downtime.
              </p>
              <p>
                <b class="mr-1">Need to add licensing details?</b>
                Just <a href="mailto:licensedetails@license.land">let us know</a> and we'll arrange it. We're also open
                to meet if you're not sure what you want.
              </p>
              <p>Licenseland takes 1% from every license purchased.</p>
              <p class="float-right small">
                <i>Only you can see this</i>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script src="https://js.stripe.com/v3/"></script>
<script>
import * as firebase from "firebase/app"
import "firebase/auth"

const scrollDown = () => window.setTimeout(() => window.scrollTo(0, document.body.scrollHeight), 50)

export default {
  created () {
    firebase.auth().onAuthStateChanged(user => this.currentUser = user)
    if (this.$route.params.status === 'success') {
      this.license = 'paid'
      this.paymentStatus = 'complete'
      this.name = localStorage.getItem('name')
      this.email = localStorage.getItem('email')
    }
    this.fetch()
  },
  data: () => ({
    currentUser: null,
    customerType: null,
    email: null,
    get: null,
    license: null,
    name: null,
    repoInfo: null,
    paymentStatus: 'notStarted',
    sourceType: null,
  }),
  methods: {
    fetch: async function() {
      this.error = this.repoInfo = null
      this.loading = true

      try {
        const response = await fetch(`/repo?repo=${this.$route.params.owner}/${this.$route.params.repo}`)

        if (response.ok) {
          this.repoInfo = await response.json()
        } else {
          const error = 'Bad response status'
          throw error
        }
      } catch(error) {
        this.error = error
      } finally {
        this.loading = false
      }
    },
    humanize: function(license) {
      switch (license) {
        case 'gplv2':
          return 'GPLv2'
        case 'gplv3':
          return 'GPLv3'
        case 'cc-by-nc':
          return 'Creative Commons'
        case 'cc-by-nc-nd':
          return 'Creative Commons'
        case 'cc-by-nc-sa':
          return 'Creative Commons'
      }
    },
    payWithStripe: async function() {
      this.paymentStatus = 'loading'
      const response = await fetch(`${process.env.VUE_APP_FIREBASE_FUNCTIONS_BASE_URL}/pay?repo=${this.repoInfo.repo}&name=${this.name}&email=${this.email}`, {
        credentials: 'include',
        method: 'POST',
      })

      if (!response.ok) {
        const error = `Stripe session creation gave a ${response.status_text}`
        throw error
      }

      const json = await response.json()

      // Initialize Stripe.js with the same connected account ID used when creating
      // the Checkout Session.
      var stripeClient = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY, {
        stripeAccount: json.stripeUserId,
      })

      stripeClient.redirectToCheckout({
        sessionId: json.stripeSessionId,
      }).then(function (result) {
        // If `redirectToCheckout` fails due to a browser or network error, display the localized error message to your
        // customer using `result.error.message`.
        return result
      })
    },
    url: function(license) {
      switch (license) {
        case 'gplv2':
          return 'https://www.gnu.org/licenses/old-licenses/gpl-2.0.en.html'
        case 'gplv3':
          return 'https://www.gnu.org/licenses/gpl-3.0.en.html'
        case 'cc-by-nc':
          return 'http://creativecommons.org/licenses/by-nc/4.0/'
        case 'cc-by-nc-nd':
          return 'http://creativecommons.org/licenses/by-nc-nd/4.0/'
        case 'cc-by-nc-sa':
          return 'http://creativecommons.org/licenses/by-nc-sa/4.0/'
      }
    },
  },
  name: 'Repo',
  props: ['repo', 'openLicense', 'closedLicense'],
  watch: {
    license: scrollDown,
    '$route': 'fetch',
  },
}
</script>
